import { ChangeEvent, DragEvent, FC, useCallback, useEffect, useState } from 'react';
import Modal from '../../modal/Modal';
import { UploadImageSVG } from '../../svgs';
import { StripeDocumentType } from '../../../types/enums';
import { StripeAccountStatus } from '../../../types/interfaces/stripeConnect.interfaces';
import { getStripeAccountStatus, uploadStripeKycDocuments } from '../../../api/stripeConnectApi';
import {
  getRequiredDocumentTypes,
  getStripeDocumentUploadHelpText,
  getStripeDocumentUploadTitle,
} from '../../../services/stripeConnect.service';
import { showToast } from '../../../services/toast.service';
import { toastMessages } from '../../../constants/errorMessages';
import { ANIMAL_CONSTANTS, BUTTON_CONSTANTS, LABEL_CONSTANTS } from '../../../constants/common';
import '../../../pages/animal-management/animal-overview/addDocumentModal.scss';

const MIN_DOCUMENT_SIZE_BYTES = 1;
const MAX_DOCUMENT_SIZE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_DOCUMENT_EXTENSION_LIST = ['pdf', 'png', 'jpeg', 'jpg'];
const ACCEPTED_DOCUMENT_EXTENSIONS = new Set(ACCEPTED_DOCUMENT_EXTENSION_LIST);
const ACCEPTED_DOCUMENT_FILE_TYPES = ACCEPTED_DOCUMENT_EXTENSION_LIST.map(extension => `.${extension}`).join(',');
const ACCEPTED_DOCUMENT_MIME_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png']);

type StripeKycDocumentModalProps = {
  isOpen: boolean;
  accountId: number;
  onClose: () => void;
  onSuccess: () => void;
  accountStatus?: StripeAccountStatus | null;
};

const isValidDocumentFile = (file: File): boolean => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  if (!fileExtension || fileExtension === 'jfif') return false;

  const hasValidExtension = ACCEPTED_DOCUMENT_EXTENSIONS.has(fileExtension);
  const hasValidMimeType = !file.type || ACCEPTED_DOCUMENT_MIME_TYPES.has(file.type);
  return hasValidExtension && hasValidMimeType;
};

const StripeKycDocumentModal: FC<StripeKycDocumentModalProps> = ({
  isOpen,
  accountId,
  onClose,
  onSuccess,
  accountStatus: initialAccountStatus,
}) => {
  const [accountStatus, setAccountStatus] = useState<StripeAccountStatus | null>(initialAccountStatus ?? null);
  const [selectedFile, setSelectedFile] = useState<File>();
  const [selectedBackFile, setSelectedBackFile] = useState<File>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const refreshAccountStatus = useCallback(async () => {
    try {
      const { data } = await getStripeAccountStatus(accountId);
      setAccountStatus(data ?? null);
    } catch {
      setAccountStatus(null);
    }
  }, [accountId]);

  useEffect(() => {
    if (initialAccountStatus) {
      setAccountStatus(initialAccountStatus);
      return;
    }
    if (!isOpen || !accountId) return;
    void refreshAccountStatus();
  }, [isOpen, accountId, initialAccountStatus, refreshAccountStatus]);

  const resetHandler = () => {
    setSelectedFile(undefined);
    setSelectedBackFile(undefined);
    setIsSubmitting(false);
    onClose();
  };

  const isAcceptableFile = (file?: File): file is File => {
    if (!file) return false;

    if (!isValidDocumentFile(file)) {
      showToast.error(toastMessages.INVALID_DOCUMENT_FILE_TYPE);
      return false;
    }
    if (file.size < MIN_DOCUMENT_SIZE_BYTES) {
      showToast.error(toastMessages.DOCUMENT_EMPTY);
      return false;
    }
    if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
      showToast.error(toastMessages.DOCUMENT_LARGER_THAN_10MB);
      return false;
    }
    return true;
  };

  const applySelectedFile = (file?: File) => {
    if (isAcceptableFile(file)) setSelectedFile(file);
  };

  const backFileChangedHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (isAcceptableFile(file)) setSelectedBackFile(file);
    event.target.value = '';
  };

  const fileChangedHandler = (event: ChangeEvent<HTMLInputElement>) => {
    applySelectedFile(event.target.files?.[0]);
    event.target.value = '';
  };

  const onDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    applySelectedFile(event.dataTransfer.files?.[0]);
  };

  const requiredDocumentTypes = getRequiredDocumentTypes(accountStatus);
  const documentType: StripeDocumentType | undefined = requiredDocumentTypes[0];
  const canSubmit = Boolean(selectedFile && documentType) && !isSubmitting;
  const helpText = getStripeDocumentUploadHelpText(documentType, accountStatus);

  const uploadHandler = async () => {
    if (!selectedFile || !documentType || isSubmitting) {
      if (!selectedFile) showToast.error(toastMessages.MISSING_REQUIRED_FIELDS);
      return;
    }

    try {
      setIsSubmitting(true);
      const backFile = documentType === StripeDocumentType.Identity ? selectedBackFile : undefined;
      const { data: newStatus } = await uploadStripeKycDocuments(accountId, selectedFile, documentType, backFile);
      setSelectedFile(undefined);
      setSelectedBackFile(undefined);

      const remainingTypes = getRequiredDocumentTypes(newStatus).filter(type => type !== documentType);
      if (remainingTypes.length > 0) {
        setAccountStatus(newStatus);
        showToast.info(LABEL_CONSTANTS.STRIPE_ADDITIONAL_DOCUMENTS_STILL_REQUIRED);
        return;
      }

      showToast.success(LABEL_CONSTANTS.STRIPE_DOCUMENT_SUBMITTED_IN_REVIEW);
      onSuccess();
    } catch (error: any) {
      showToast.error(error?.response?.data?.title || error?.message || toastMessages.SOMETHING_WENT_WRONG);
      await refreshAccountStatus();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={resetHandler} ignoreBackdrop width={556}>
      <div className="add-document-modal">
        <h2>{documentType ? getStripeDocumentUploadTitle(documentType) : ANIMAL_CONSTANTS.UPLOAD_DOCUMENT}</h2>
        <p className="kyc-document-description">{helpText}</p>
        {requiredDocumentTypes.length > 1 && (
          <p className="kyc-document-description">
            {LABEL_CONSTANTS.STRIPE_DOCUMENTS_REMAINING.replace('{count}', String(requiredDocumentTypes.length))}
          </p>
        )}

        <input
          id="stripe-kyc-upload-document"
          type="file"
          className="upload-document-input"
          accept={ACCEPTED_DOCUMENT_FILE_TYPES}
          onChange={fileChangedHandler}
        />

        <label
          htmlFor="stripe-kyc-upload-document"
          className="drop-area"
          onDragOver={event => event.preventDefault()}
          onDrop={onDrop}>
          <UploadImageSVG className="upload-icon" />
          <p className="drag-n-drop-text">{ANIMAL_CONSTANTS.DRAG_DROP_A_FILE}</p>
          <p className="supported-formats-text">
            {ANIMAL_CONSTANTS.DOCUMENT_SUPPORTED_FORMATS} <strong>{ANIMAL_CONSTANTS.DOCUMENT_SUPPORTED_FORMATS_LIST}</strong>
          </p>
          <p className="upload-limits-text">{LABEL_CONSTANTS.STRIPE_DOCUMENT_UPLOAD_FILE_LIMIT}</p>
          {selectedFile && <p className="selected-file-name">{selectedFile.name}</p>}
        </label>

        {documentType === StripeDocumentType.Identity && (
          <div className="kyc-document-back">
            <label htmlFor="stripe-kyc-upload-document-back" className="kyc-document-description">
              {LABEL_CONSTANTS.STRIPE_ID_BACK_OPTIONAL}
            </label>
            <input
              id="stripe-kyc-upload-document-back"
              type="file"
              accept={ACCEPTED_DOCUMENT_FILE_TYPES}
              onChange={backFileChangedHandler}
            />
            {selectedBackFile && <p className="selected-file-name">{selectedBackFile.name}</p>}
          </div>
        )}

        <div className="modal-footer">
          <button type="button" className="red button medium" onClick={resetHandler} disabled={isSubmitting}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
          <button
            type="button"
            className={canSubmit ? 'green button medium' : 'green button medium disabled'}
            onClick={() => void uploadHandler()}
            disabled={!canSubmit}>
            {BUTTON_CONSTANTS.UPDATE}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default StripeKycDocumentModal;
